import { VariableKind } from '../constants'
import { useEffect, useMemo, useState } from 'react'
import { TemplateNoStoreSetting } from '../components/NoStoreSettings'
import { useParams, useNavigate, useSearchParams } from 'react-router'
import { useIsMobile } from '../hooks/useMediaQuery'
import { templatesApiService } from '../services/templatesApiService'
import { foldersApiService } from '../services/foldersApiService'
import { renderApiService, type CompareVersionResult } from '../services/renderApiService'
import { ApiError } from '../services/BaseApiService'
import type { Folder, Template, TemplateVersion, TemplateVariable } from '../types'
import { colors, fontFamily, fontSize, radius, shadow, spacing, tracking, transition } from '../styles/theme'
import { Button, StatusChip, VersionChip, Select, Badge, ConfirmModal, Textarea, Modal, Input } from '../components/ui'
import { templateStatusLabel } from '../utils/templateStatus'
import { ancestorChain } from '../utils/folderTree'
import { usePublishBreadcrumb } from '../contexts/BreadcrumbContext'
import { useIsLiveWorkspace } from '../contexts/AppStateContext'
import PromoteToLiveModal from '../components/templates/PromoteToLiveModal'
import CopyVersionModal from '../components/templates/CopyVersionModal'
import LiveReadOnlyBanner from '../components/templates/LiveReadOnlyBanner'
import { PendingPromotionRequestBanner, TemplatePromotionApprovalSetting } from '../components/templates/PromotionApproval'
import { useIsTeamAdmin } from '../hooks/useCurrentUser'
import { TabStrip } from '../components/analytics/AnalyticsUi'
import TemplateAnalyticsPanel from '../components/analytics/TemplateAnalyticsPanel'

function variableKindLabel(id: number): string {
  const map: Record<number, string> = { [VariableKind.SCALAR]: 'Scalar', [VariableKind.LOOP]: 'Loop', [VariableKind.CONDITIONAL]: 'Conditional' }
  return map[id] ?? String(id)
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

interface RenderModalProps {
  version: TemplateVersion
  onClose: () => void
}

function RenderModal({ version, onClose }: RenderModalProps) {
  const [payload, setPayload] = useState('{\n  \n}')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleRender() {
    setError('')
    try {
      JSON.parse(payload)
    } catch {
      setError('Invalid JSON payload')
      return
    }
    setLoading(true)
    try {
      const blob = await renderApiService.generate(version.templateVersionId, payload)
      downloadBlob(blob, `render-${version.templateVersionId}.pdf`)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Render failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: colors.overlay,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: colors.surface,
        borderRadius: radius.lg,
        boxShadow: shadow.lg,
        padding: spacing.xl,
        width: 560,
        maxWidth: '90vw',
      }}>
        <div style={{ fontSize: fontSize.lg, fontWeight: 700, color: colors.text, marginBottom: spacing.xs }}>
          Render Template
        </div>
        <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.lg }}>
          Version {version.versionNumber}
        </div>

        {error && (
          <div style={{
            background: colors.errorLight, color: colors.error,
            border: `1px solid ${colors.errorBorder}`,
            borderRadius: radius.sm,
            padding: `${spacing.sm} ${spacing.md}`,
            fontSize: fontSize.sm, marginBottom: spacing.md,
          }}>{error}</div>
        )}

        <div style={{ marginBottom: spacing.lg }}>
          <label style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 500, marginBottom: spacing.xs }}>
            JSON Payload
          </label>
          <textarea
            value={payload}
            onChange={e => setPayload(e.target.value)}
            rows={10}
            style={{
              width: '100%',
              padding: '8px 10px',
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              fontFamily: fontFamily.mono,
              color: colors.text,
              boxSizing: 'border-box',
              resize: 'vertical',
              outline: 'none',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={loading} onClick={handleRender}>
            {loading ? 'Rendering...' : 'Render & Download PDF'}
          </Button>
        </div>
      </div>
    </div>
  )
}

interface CompareModalProps {
  currentVersion: TemplateVersion
  allVersions: TemplateVersion[]
  onClose: () => void
}

function CompareModal({ currentVersion, allVersions, onClose }: CompareModalProps) {
  const others = allVersions.filter(v => v.templateVersionId !== currentVersion.templateVersionId)
  const [compareVersionId, setCompareVersionId] = useState(others[0]?.templateVersionId ?? '')
  const [payload, setPayload] = useState('{\n  \n}')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<{ version1: CompareVersionResult; version2: CompareVersionResult } | null>(null)

  async function handleCompare() {
    if (!compareVersionId) return
    setError(''); setLoading(true); setResult(null)
    try {
      const res = await renderApiService.compareVersions(currentVersion.templateVersionId, compareVersionId, { payloadJson: payload })
      setResult(res)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Compare failed')
    } finally {
      setLoading(false)
    }
  }

  function renderPane(r: CompareVersionResult) {
    if (!r.success) return (
      <div style={{ color: colors.error, fontSize: fontSize.sm, padding: spacing.md }}>{r.error ?? 'Render failed'}</div>
    )
    if (!r.content) return null
    const src = `data:${r.contentType ?? 'application/pdf'};base64,${r.content}`
    if (r.contentType?.includes('pdf')) {
      return <iframe src={src} style={{ width: '100%', height: 500, border: 'none', borderRadius: radius.sm }} title={`v${r.versionNumber}`} />
    }
    const decoded = atob(r.content)
    return (
      <pre style={{
        margin: 0, padding: spacing.md, fontSize: fontSize.xs,
        fontFamily: fontFamily.mono,
        lineHeight: 1.5, overflowY: 'auto', maxHeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {decoded}
      </pre>
    )
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: colors.overlay,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 1000, overflowY: 'auto', padding: spacing.lg,
    }}>
      <div style={{
        background: colors.surface, borderRadius: radius.lg, boxShadow: shadow.lg,
        width: '95vw', maxWidth: 1200, padding: spacing.xl,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
          <div style={{ fontSize: fontSize.lg, fontWeight: 700, color: colors.text }}>
            Compare Versions
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: colors.textSecondary }}>✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg, marginBottom: spacing.md }}>
          <div>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, marginBottom: spacing.xs }}>
              Version A (selected)
            </div>
            <div style={{
              padding: spacing.sm, background: colors.primaryLight, border: `1px solid ${colors.primary}`,
              borderRadius: radius.sm, fontSize: fontSize.base, fontWeight: 600, color: colors.primary,
            }}>
              v{currentVersion.versionNumber}
              {currentVersion.isActive && <span style={{ marginLeft: spacing.xs, fontSize: fontSize.xs }}>· Live</span>}
            </div>
          </div>
          <div>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, marginBottom: spacing.xs }}>
              Version B
            </div>
            <Select
              placeholder="No other versions"
              isDisabled={others.length === 0}
              value={compareVersionId}
              onChange={v => { setCompareVersionId(String(v)); setResult(null) }}
              options={others.map(v => ({ value: v.templateVersionId, label: `v${v.versionNumber}${v.isActive ? ' (Live)' : ''}` }))}
            />
          </div>
        </div>

        <div style={{ marginBottom: spacing.md }}>
          <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, marginBottom: spacing.xs }}>
            Payload JSON (optional)
          </div>
          <textarea
            value={payload}
            onChange={e => { setPayload(e.target.value); setResult(null) }}
            rows={3}
            style={{
              width: '100%', padding: spacing.sm, boxSizing: 'border-box',
              border: `1px solid ${colors.border}`, borderRadius: radius.sm,
              fontSize: fontSize.xs, fontFamily: fontFamily.mono,
              color: colors.text, background: colors.background, resize: 'vertical', outline: 'none',
            }}
            spellCheck={false}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.lg }}>
          <Button
            loading={loading}
            disabled={!compareVersionId}
            onClick={handleCompare}
          >
            {loading ? 'Rendering…' : 'Compare'}
          </Button>
          {error && <span style={{ fontSize: fontSize.sm, color: colors.error }}>{error}</span>}
        </div>

        {result && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg }}>
            {[result.version1, result.version2].map((r, i) => (
              <div key={i} style={{
                border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden',
              }}>
                <div style={{
                  padding: `${spacing.xs} ${spacing.md}`, background: colors.background,
                  borderBottom: `1px solid ${colors.border}`,
                  fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary,
                }}>
                  v{r.versionNumber} {i === 0 ? '(A)' : '(B)'}
                </div>
                {renderPane(r)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default function TemplateDetailPage() {
  const { templateId } = useParams<{ templateId: string }>()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'analytics' ? 'analytics' : 'details'
  const [template, setTemplate] = useState<Template | null>(null)
  const [versions, setVersions] = useState<TemplateVersion[]>([])
  const [selectedVersion, setSelectedVersion] = useState<TemplateVersion | null>(null)
  const [variables, setVariables] = useState<TemplateVariable[]>([])
  const [loading, setLoading] = useState(true)
  const [varsLoading, setVarsLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState('')
  const [actionError, setActionError] = useState('')
  const [showRender, setShowRender] = useState(false)
  const [showCompare, setShowCompare] = useState(false)
  const [promoteTarget, setPromoteTarget] = useState<TemplateVersion | null>(null)
  const [copyTarget, setCopyTarget] = useState<TemplateVersion | null>(null)
  const [showDemote, setShowDemote] = useState(false)
  const [demoteNote, setDemoteNote] = useState('')
  const [notice, setNotice] = useState('')
  const [renameValue, setRenameValue] = useState<string | null>(null)
  const [renameError, setRenameError] = useState('')
  const isLiveWorkspace = useIsLiveWorkspace()
  const isTeamAdmin = useIsTeamAdmin()
  const [retentionDays, setRetentionDays] = useState<string>('')
  const [retentionSaving, setRetentionSaving] = useState(false)
  const [legalHoldSaving, setLegalHoldSaving] = useState(false)
  const [storageRegion, setStorageRegion] = useState<string>('')
  const [storageRegionSaving, setStorageRegionSaving] = useState(false)
  const [hoveredVar, setHoveredVar] = useState<string | null>(null)
  const [folders, setFolders] = useState<Folder[]>([])

  // Folder chain for the top-bar breadcrumb. Fetched separately from the template because
  // templates/get returns only a folderId, not its ancestry.
  useEffect(() => {
    foldersApiService.listAll().then(setFolders).catch(() => setFolders([]))
  }, [])

  // Templates › folder1 › sub2 › sub3 › <template name>. Each folder crumb opens that folder in
  // the tree; the template itself is the non-clickable leaf. Published as null until the template
  // has loaded so the route-derived trail stays up rather than the bar flashing empty.
  usePublishBreadcrumb(useMemo(() => {
    if (!template) return null
    return [
      { label: 'Templates', to: '/app/templates' },
      ...ancestorChain(folders, template.folderId).map(f => ({
        label: f.name,
        to: `/app/templates?folderId=${f.folderId}`,
      })),
      { label: template.name },
    ]
  }, [template, folders]))

  async function handleRename() {
    const name = renameValue?.trim()
    if (!template || !name) return
    setRenameError(''); setActionLoading(true)
    try {
      await templatesApiService.update(template, name)
      setRenameValue(null)
      await loadData()
    } catch (err) {
      setRenameError(err instanceof ApiError ? err.message : 'Rename failed')
    } finally {
      setActionLoading(false)
    }
  }

  async function loadData() {
    if (!templateId) return
    try {
      const [t, v] = await Promise.all([
        templatesApiService.getById(templateId),
        templatesApiService.listVersions(templateId),
      ])
      setTemplate(t)
      setRetentionDays(t.retentionDays != null ? String(t.retentionDays) : '')
      setStorageRegion(t.storageRegion ?? '')
      setVersions(v)
      setSelectedVersion(prev => {
        if (!prev) {
          return v.length > 0 ? v.reduce((a, b) => a.versionNumber > b.versionNumber ? a : b) : null
        }
        return v.find(x => x.templateVersionId === prev.templateVersionId) ?? prev
      })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load template')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadData() }, [templateId])

  useEffect(() => {
    if (!selectedVersion) return
    setVarsLoading(true)
    templatesApiService.getVariables(selectedVersion.templateVersionId)
      .then(setVariables)
      .catch(() => setVariables([]))
      .finally(() => setVarsLoading(false))
  }, [selectedVersion])

  function handle409(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 409) {
      setActionError('Version was modified by another request. Reloading...')
      loadData()
      return true
    }
    return false
  }

  async function handleCancelSchedule() {
    if (!template) return
    setActionError(''); setNotice(''); setActionLoading(true)
    try {
      await templatesApiService.cancelScheduledPromotion(template.templateId)
      setNotice('Scheduled promotion cancelled.')
      await loadData()
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Cancel failed')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleDemote() {
    if (!template) return
    setActionError(''); setNotice(''); setActionLoading(true)
    try {
      await templatesApiService.demote(template.templateId, demoteNote.trim() || undefined)
      setShowDemote(false)
      setDemoteNote('')
      setNotice('Removed from Live. No version of this template is live now.')
      await loadData()
    } catch (err) {
      setShowDemote(false)
      if (!handle409(err)) setActionError(err instanceof ApiError ? err.message : 'Demote failed')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleToggleLegalHold() {
    if (!template) return
    setLegalHoldSaving(true)
    try {
      await templatesApiService.setLegalHold(template.templateId, !template.legalHold)
      await loadData()
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to update legal hold')
    } finally {
      setLegalHoldSaving(false)
    }
  }

  async function handleSaveStorageRegion() {
    if (!template) return
    setStorageRegionSaving(true)
    try {
      await templatesApiService.setStorageRegion(template.templateId, storageRegion.trim() || null)
      await loadData()
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to update storage region')
    } finally {
      setStorageRegionSaving(false)
    }
  }

  async function handleSaveRetention() {
    if (!template) return
    setRetentionSaving(true)
    try {
      const days = retentionDays.trim() === '' ? null : parseInt(retentionDays, 10)
      await templatesApiService.setRetention(template.templateId, isNaN(days as number) ? null : days)
      await loadData()
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to save retention')
    } finally {
      setRetentionSaving(false)
    }
  }

  const thStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    textAlign: 'left',
    fontFamily: fontFamily.mono,
    fontSize: fontSize.xs,
    fontWeight: 500,
    color: colors.textMuted,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${colors.border}`,
    background: colors.background,
  }

  const tdStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: fontSize.base,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
  }

  if (loading) return <div style={{ color: colors.textSecondary }}>Loading...</div>
  if (error) return <div style={{ color: colors.error }}>{error}</div>
  if (!template) return null

  const liveVersionId = template.liveVersionId ?? versions.find(v => v.isActive)?.templateVersionId ?? null
  const liveVersion = liveVersionId ? versions.find(v => v.templateVersionId === liveVersionId) ?? null : null
  const pending = template.pendingPromotion ?? null
  const pendingVersion = pending ? versions.find(v => v.templateVersionId === pending.templateVersionId) ?? null : null
  const isLiveVersion = (v: TemplateVersion) => v.templateVersionId === liveVersionId
  const approvalRule = template.promotionApproval ?? null
  const pendingRequest = template.pendingPromotionRequest ?? null
  const approvalLevel = approvalRule?.level ?? 'auto'

  return (
    <div>
      {isLiveWorkspace && <LiveReadOnlyBanner />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.xl }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, margin: 0, letterSpacing: tracking.tight }}>
              {template.name}
            </h1>
            {!isLiveWorkspace && (
              <Button size="sm" variant="ghost" onClick={() => { setRenameError(''); setRenameValue(template.name) }}>Rename</Button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, marginTop: spacing.xs, flexWrap: 'wrap' }}>
            <StatusChip status={templateStatusLabel(template.templateStatusLookupId)} />
            <span data-testid="live-status" style={{ fontSize: fontSize.sm, color: liveVersionId ? colors.success : colors.textSecondary, fontWeight: 500 }}>
              {liveVersionId ? (liveVersion ? `Live: v${liveVersion.versionNumber}` : 'Live') : 'Not in Live'}
            </span>
          </div>
          {!isLiveWorkspace && pending && (
            <div data-testid="pending-promotion" style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xs, fontSize: fontSize.sm, color: colors.warning }}>
              <span>
                Scheduled: {pendingVersion ? `v${pendingVersion.versionNumber}` : 'a version'} goes live at {new Date(pending.promoteAt).toLocaleString()}
              </span>
              <Button size="sm" variant="ghost" onClick={handleCancelSchedule} disabled={actionLoading}>
                Cancel
              </Button>
            </div>
          )}
          {template.description && (
            <p style={{ color: colors.textSecondary, marginTop: spacing.xs, fontSize: fontSize.base }}>
              {template.description}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: spacing.sm }}>
          {selectedVersion && (
            <>
              {!isLiveWorkspace && <Button
                variant="secondary"
                // The selected version is passed explicitly. This button sits inside
                // `selectedVersion && ...`, so the page already knows exactly which version the
                // user is looking at — dropping it and letting the editor work out which to open
                // meant the editor had to guess, and guessed wrong.
                onClick={() => navigate(
                  `/app/templates/${templateId}/edit?versionId=${selectedVersion.templateVersionId}`)}
              >
                Edit
              </Button>}
              <Button
                variant="primary"
                onClick={() => navigate(`/app/templates/${templateId}/preview`)}
              >
                Preview
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowRender(true)}
              >
                Render & Download
              </Button>
            </>
          )}
          {liveVersionId && (
            <Button variant="danger" onClick={() => setShowDemote(true)} disabled={actionLoading}>
              Demote from Live
            </Button>
          )}
        </div>
      </div>

      {!isLiveWorkspace && pendingRequest && (
        <PendingPromotionRequestBanner
          request={pendingRequest}
          onChanged={message => { setActionError(''); setNotice(message); loadData() }}
        />
      )}

      {actionError && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`,
          borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{actionError}</div>
      )}
      {notice && (
        <div style={{
          background: colors.successLight, color: colors.success,
          border: `1px solid ${colors.successBorder}`,
          borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{notice}</div>
      )}

      <TabStrip
        tabs={[{ id: 'details', label: 'Details' }, { id: 'analytics', label: 'Analytics' }]}
        active={tab}
        onChange={id => setParams(id === 'analytics' ? { tab: 'analytics' } : {}, { replace: true })}
      />

      {tab === 'analytics' && templateId ? <TemplateAnalyticsPanel templateId={templateId} /> : (
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '300px 1fr', gap: spacing.lg }}>
        {/* Version list */}
        <div>
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            boxShadow: shadow.sm,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: `${spacing.sm} ${spacing.md}`,
              background: colors.background,
              borderBottom: `1px solid ${colors.border}`,
              fontSize: fontSize.sm,
              fontWeight: 600,
              color: colors.textSecondary,
            }}>
              Versions
            </div>
            {versions.length === 0 ? (
              <div style={{ padding: spacing.md, color: colors.textSecondary, fontSize: fontSize.base }}>
                No versions yet.
              </div>
            ) : (
              versions.map(v => {
                const isSelected = selectedVersion?.templateVersionId === v.templateVersionId
                const live = isLiveVersion(v)
                return (
                  <div
                    key={v.templateVersionId}
                    data-testid="version-row"
                    onClick={() => setSelectedVersion(v)}
                    style={{
                      padding: `${spacing.sm} ${spacing.md}`,
                      paddingLeft: live ? `calc(${spacing.md} - 3px)` : spacing.md,
                      borderBottom: `1px solid ${colors.border}`,
                      borderLeft: live ? `3px solid ${colors.primary}` : '3px solid transparent',
                      cursor: 'pointer',
                      background: isSelected ? colors.background : live ? colors.primaryLight : 'transparent',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: `background ${transition.fast}`,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                      <VersionChip version={v.versionNumber} active={live} />
                      <span style={{ fontSize: fontSize.xs, color: colors.textMuted }}>
                        {new Date(v.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {live && <LiveBadge />}
                      {pending?.templateVersionId === v.templateVersionId && (
                        <Badge color={colors.warning} bg={colors.warningLight} border={colors.warningBorder}>Scheduled</Badge>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Version detail + variables */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {/* Version actions */}
          {selectedVersion && (
            <div style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.md,
              boxShadow: shadow.sm,
              padding: spacing.md,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                    <VersionChip version={selectedVersion.versionNumber} active={isLiveVersion(selectedVersion)} />
                    {isLiveVersion(selectedVersion) && <LiveBadge />}
                  </div>
                  {selectedVersion.changeNote && (
                    <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: 2 }}>
                      {selectedVersion.changeNote}
                    </div>
                  )}
                  {isLiveVersion(selectedVersion) && selectedVersion.activatedAt && (
                    <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: 2 }}>
                      Promoted to Live {new Date(selectedVersion.activatedAt).toLocaleString()}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  {!isLiveWorkspace && !isLiveVersion(selectedVersion) && (
                    <Button
                      size="sm"
                      onClick={() => setPromoteTarget(selectedVersion)}
                      disabled={actionLoading}
                    >
                      {approvalLevel === 'auto' ? 'Promote to Live' : 'Request promotion'}
                    </Button>
                  )}
                  {versions.length > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowCompare(true)}
                    >
                      Compare
                    </Button>
                  )}
                  {!isLiveWorkspace && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setCopyTarget(selectedVersion)}
                    >
                      Copy as new template
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {!isLiveWorkspace && approvalRule && (
            <TemplatePromotionApprovalSetting
              templateId={template.templateId}
              rule={approvalRule}
              folders={folders}
              isAdmin={isTeamAdmin}
              onSaved={rule => setTemplate(t => t ? { ...t, promotionApproval: rule } : t)}
            />
          )}

          {!isLiveWorkspace && <TemplateNoStoreSetting templateId={template.templateId} />}

          {/* Retention settings */}
          {!isLiveWorkspace && (<>
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            boxShadow: shadow.sm,
            padding: spacing.md,
          }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>
              Output Retention
            </div>
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.sm }}>
              How long to keep render output blobs. Leave blank to keep forever.
            </div>
            <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
              <input
                type="number"
                min={1}
                max={3650}
                placeholder="Days (blank = forever)"
                value={retentionDays}
                onChange={e => setRetentionDays(e.target.value)}
                style={{
                  width: 200,
                  padding: '6px 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  color: colors.text,
                }}
              />
              <Button
                size="sm"
                loading={retentionSaving}
                disabled={retentionSaving}
                onClick={handleSaveRetention}
              >
                Save
              </Button>
              {template?.retentionDays != null && (
                <span style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                  Current: {template.retentionDays}d
                </span>
              )}
            </div>
          </div>

          {/* Compliance settings */}
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            boxShadow: shadow.sm,
            padding: spacing.md,
          }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>
              Compliance
            </div>

            {/* Legal hold */}
            <div style={{ marginBottom: spacing.md }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                <button
                  onClick={handleToggleLegalHold}
                  disabled={legalHoldSaving}
                  style={{
                    width: 40, height: 22, borderRadius: 11,
                    background: template?.legalHold ? colors.error : colors.border,
                    border: 'none', cursor: legalHoldSaving ? 'default' : 'pointer',
                    position: 'relative', transition: `background ${transition.fast}`,
                    opacity: legalHoldSaving ? 0.6 : 1,
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 3,
                    left: template?.legalHold ? 21 : 3,
                    width: 16, height: 16, borderRadius: '50%',
                    background: colors.surface, transition: `left ${transition.fast}`,
                  }} />
                </button>
                <div>
                  <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: template?.legalHold ? colors.error : colors.text }}>
                    Legal Hold {template?.legalHold ? '(Active)' : '(Off)'}
                  </div>
                  <div style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                    When active, render outputs are exempt from retention purges regardless of retention period.
                  </div>
                </div>
              </div>
            </div>

            {/* Storage region */}
            <div>
              <div style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>
                Storage Region Preference
              </div>
              <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="e.g. us-east, eu-west (blank = default)"
                  value={storageRegion}
                  onChange={e => setStorageRegion(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: radius.sm,
                    fontSize: fontSize.sm,
                    color: colors.text,
                    background: colors.background,
                  }}
                />
                <Button
                  size="sm"
                  loading={storageRegionSaving}
                  disabled={storageRegionSaving}
                  onClick={handleSaveStorageRegion}
                >
                  Save
                </Button>
                {template?.storageRegion && (
                  <span style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                    Current: {template.storageRegion}
                  </span>
                )}
              </div>
            </div>
          </div>
          </>)}

          {/* Variables table */}
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            boxShadow: shadow.sm,
            overflow: 'hidden',
          }}>
            <div style={{
              padding: `${spacing.sm} ${spacing.md}`,
              background: colors.background,
              borderBottom: `1px solid ${colors.border}`,
              fontSize: fontSize.sm,
              fontWeight: 600,
              color: colors.textSecondary,
            }}>
              Variables {selectedVersion && `(v${selectedVersion.versionNumber})`}
            </div>
            {!selectedVersion ? (
              <div style={{ padding: spacing.md, color: colors.textSecondary }}>Select a version to view variables.</div>
            ) : varsLoading ? (
              <div style={{ padding: spacing.md, color: colors.textSecondary }}>Loading...</div>
            ) : variables.length === 0 ? (
              <div style={{ padding: spacing.md, color: colors.textSecondary }}>No variables found.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Key</th>
                    <th style={thStyle}>Raw Name</th>
                    <th style={thStyle}>Kind</th>
                    <th style={thStyle}>Occurrences</th>
                    <th style={thStyle}>Context</th>
                  </tr>
                </thead>
                <tbody>
                  {variables.map(v => (
                    <tr
                      key={v.normalizedKey}
                      onMouseEnter={() => setHoveredVar(v.normalizedKey)}
                      onMouseLeave={() => setHoveredVar(null)}
                      style={{
                        background: hoveredVar === v.normalizedKey ? colors.surfaceMist : 'transparent',
                        transition: `background ${transition.fast}`,
                      }}
                    >
                      <td style={{ ...tdStyle, fontFamily: fontFamily.mono, fontSize: fontSize.sm }}>{v.normalizedKey}</td>
                      <td style={{ ...tdStyle, fontFamily: fontFamily.mono, fontSize: fontSize.sm }}>{v.rawName}</td>
                      <td style={tdStyle}>{variableKindLabel(v.variableKindLookupId)}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{v.occurrenceCount}</td>
                      <td style={{ ...tdStyle, color: colors.textSecondary, fontSize: fontSize.sm }}>
                        {v.contextSnippet || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      )}

      {showRender && selectedVersion && (
        <RenderModal version={selectedVersion} onClose={() => setShowRender(false)} />
      )}
      {showCompare && selectedVersion && (
        <CompareModal
          currentVersion={selectedVersion}
          allVersions={versions}
          onClose={() => setShowCompare(false)}
        />
      )}
      {promoteTarget && (
        <PromoteToLiveModal
          version={promoteTarget}
          liveVersionNumber={liveVersion?.versionNumber ?? null}
          approvalLevel={approvalLevel}
          onClose={() => setPromoteTarget(null)}
          onDone={result => {
            setActionError('')
            setNotice('approvalRequired' in result && result.approvalRequired
              ? result.approvalLevel === 'self'
                ? `Request created for v${result.versionNumber} — approve it from the link in your email or on the Approvals page.`
                : `Approval requested for v${result.versionNumber} — approvers have been notified.`
              : result.scheduled
                ? `v${result.versionNumber} is scheduled to go live at ${new Date(result.promoteAt).toLocaleString()}.`
                : `v${result.versionNumber} is now live.`)
            loadData()
          }}
        />
      )}
      {copyTarget && (
        <CopyVersionModal
          version={copyTarget}
          templateName={template.name}
          folders={folders}
          defaultFolderId={template.folderId}
          onClose={() => setCopyTarget(null)}
          onCopied={newId => { setCopyTarget(null); navigate(`/app/templates/${newId}`) }}
        />
      )}
      {renameValue !== null && (
        <Modal
          open
          onClose={() => setRenameValue(null)}
          title="Rename Template"
          width={420}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRenameValue(null)} disabled={actionLoading}>Cancel</Button>
              <Button variant="primary" onClick={handleRename} loading={actionLoading}
                disabled={!renameValue.trim() || renameValue.trim() === template.name}>
                Save
              </Button>
            </>
          }
        >
          <Input
            label="Template name"
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void handleRename() }}
            error={renameError || undefined}
            autoFocus
          />
        </Modal>
      )}
      {showDemote && (
        <ConfirmModal
          open
          danger
          loading={actionLoading}
          title="Demote from Live?"
          confirmLabel="Demote"
          message={
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
              <span>
                {liveVersion ? `v${liveVersion.versionNumber}` : 'The live version'} is removed from the Live
                workspace and no version of this template will be live until one is promoted again.
              </span>
              <Textarea
                aria-label="Change note"
                placeholder="Change note (optional)"
                value={demoteNote}
                onChange={e => setDemoteNote(e.target.value)}
                rows={2}
              />
            </div>
          }
          onConfirm={handleDemote}
          onCancel={() => { setShowDemote(false); setDemoteNote('') }}
        />
      )}
    </div>
  )
}

function LiveBadge() {
  return (
    <Badge color={colors.success} bg={colors.successLight} border={colors.successBorder}>Live</Badge>
  )
}
